import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { isPlatformBrowser, ViewportScroller } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ScrollService {
  isBrowser = false;
  
  constructor(
    private router: Router,
    private readonly viewport: ViewportScroller,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    
    // Solo ejecutar en el navegador, no en SSR
    if (this.isBrowser) {
      // Escuchar todos los eventos de navegación
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          // Esperar múltiples ciclos para asegurar que todo esté cargado
          this.forceScrollToTop();
        });
    }
  }

  /**
   * Forzar scroll al top con múltiples intentos
   */
  private forceScrollToTop(): void {

    // Intento inmediato
    this.scrollToTopImmediate();

    // Intentos con delays escalonados
    const delays = [50, 100, 200, 300, 500, 1000];

    delays.forEach((delay) => {
      setTimeout(() => {
        this.scrollToTopImmediate();
      }, delay);
    });
  }

  /**
   * Método público para scroll al top con evento (compatible con Scrolling service)
   */
  toTop(event?: any): void {
    if (this.isBrowser) {
      // Intento inmediato
      this.performScroll();

      // Intentos con delays para asegurar que funcione
      const delays = [50, 100, 200, 500, 1000];

      delays.forEach((delay) => {
        setTimeout(() => {
          this.performScroll();
        }, delay);
      });
    }
  }

  /**
   * Método privado unificado para realizar scroll
   */
  private performScroll(): void {
    // Método 1: window.scrollTo
    window.scrollTo(0, 0);

    // Método 2: document elements
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Método 3: ViewportScroller
    this.viewport.scrollToPosition([0, 0]);

    // Método 4: scrollingElement
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }

    // Método 5: Elemento específico de la app
    const sidenavContent = document.getElementById('sidenav-content-global');
    if (sidenavContent) {
      sidenavContent.scrollTop = 0;
    }

    // Método 6: Buscar elementos de Material Angular
    const matSidenavContents = document.querySelectorAll('mat-sidenav-content');
    matSidenavContents.forEach((el: any) => {
      if (el.scrollTop > 0) {
        el.scrollTop = 0;
      }
    });

    // Método 7: Buscar cualquier elemento con clase que contenga 'content'
    const contentElements = document.querySelectorAll('[class*="content"]');
    contentElements.forEach((el: any) => {
      if (el.scrollTop > 0) {
        el.scrollTop = 0;
      }
    });

    // Método 8: Buscar elementos con scroll activo
    const allElements = document.querySelectorAll('*');
    allElements.forEach((el: any) => {
      if (el.scrollTop > 0) {
        el.scrollTop = 0;
      }
    });
  }

  /**
   * Método para scroll manual al top con animación suave
   */
  scrollToTop(): void {
    if (this.isBrowser) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }

  /**
   * Método para scroll inmediato al top
   */
  scrollToTopImmediate(): void {
    if (this.isBrowser) {
      this.performScroll();
    }
  }

  /**
   * Obtener información del navegador (método del Scrolling service)
   */
  getBrowser(): number {
    let userAgent = navigator.userAgent;
    if (userAgent.match(/chrome|chromium|crios/i)) {
      return 1;
    } else if (userAgent.match(/firefox|fxios/i)) {
      return 2;
    } else if (userAgent.match(/safari/i)) {
      return 3;
    } else if (userAgent.match(/opr\//i)) {
      return 4;
    } else if (userAgent.match(/edg/i)) {
      return 5;
    } else {
      return 2;
    }
  }
}
